import { useLocation, useNavigate } from 'react-router-dom';
import { useEffect } from 'react';

interface SummaryData {
  sessionId: number;
  accuracy: number;
  speedScore: number;
  correctAnswers: number;
  wrongAnswers: number;
  skippedAnswers: number;
  totalQuestions: number;
  totalTime: number;
  averageTime: number;
  xpEarned: number;
  aiFeedback: string;
  topicProgress?: {
    topic: string;
    accuracy: number;
    masteryLevel: string;
    weaknessScore: number;
  };
}

const PracticeSummaryPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const summaryData = location.state as SummaryData;

  useEffect(() => {
    if (!summaryData) {
      navigate('/practice');
    }
  }, [summaryData, navigate]);

  if (!summaryData) {
    return null;
  }

  const getMasteryColor = (level: string) => {
    const colors: Record<string, string> = {
      beginner: 'text-gray-600 bg-gray-100',
      improving: 'text-blue-600 bg-blue-100',
      strong: 'text-green-600 bg-green-100',
      mastered: 'text-purple-600 bg-purple-100',
    };
    return colors[level] || 'text-gray-600 bg-gray-100';
  };

  const getMasteryIcon = (level: string) => {
    const icons: Record<string, string> = {
      beginner: '🌱',
      improving: '📈',
      strong: '💪',
      mastered: '🏆',
    };
    return icons[level] || '📝';
  };

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}m ${secs}s`;
  };

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-4xl mx-auto px-4">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="text-6xl mb-4">
            {summaryData.accuracy >= 80 ? '🎉' : summaryData.accuracy >= 60 ? '👏' : '💪'}
          </div>
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Practice Complete!</h1>
          <p className="text-gray-600">Here's how you performed</p>
        </div>

        {/* Score Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
          <div className="bg-white rounded-lg shadow-md p-6 text-center">
            <div className="text-4xl font-bold text-blue-600 mb-2">
              {summaryData.accuracy.toFixed(1)}%
            </div>
            <div className="text-gray-600">Accuracy</div>
            <div className="mt-2 text-sm text-gray-500">
              {summaryData.correctAnswers} / {summaryData.totalQuestions} correct
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-md p-6 text-center">
            <div className="text-4xl font-bold text-purple-600 mb-2">
              {summaryData.xpEarned} XP
            </div>
            <div className="text-gray-600">Experience Earned</div>
            <div className="mt-2 text-sm text-gray-500">
              Keep practicing to earn more!
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-md p-6 text-center">
            <div className="text-4xl font-bold text-green-600 mb-2">
              {summaryData.speedScore.toFixed(0)}
            </div>
            <div className="text-gray-600">Speed Score</div>
            <div className="mt-2 text-sm text-gray-500">
              Avg: {Math.round(summaryData.averageTime)}s per question
            </div>
          </div>
        </div>

        {/* Detailed Stats */}
        <div className="bg-white rounded-lg shadow-md p-6 mb-8">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">📊 Detailed Statistics</h2>
          <div className="space-y-4">
            <div className="flex items-center justify-between p-4 bg-green-50 rounded-lg">
              <div className="flex items-center">
                <span className="text-2xl mr-3">✅</span>
                <span className="font-medium text-gray-900">Correct Answers</span>
              </div>
              <span className="text-xl font-bold text-green-600">{summaryData.correctAnswers}</span>
            </div>

            <div className="flex items-center justify-between p-4 bg-red-50 rounded-lg">
              <div className="flex items-center">
                <span className="text-2xl mr-3">❌</span>
                <span className="font-medium text-gray-900">Wrong Answers</span>
              </div>
              <span className="text-xl font-bold text-red-600">{summaryData.wrongAnswers}</span>
            </div>

            <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
              <div className="flex items-center">
                <span className="text-2xl mr-3">⏭️</span>
                <span className="font-medium text-gray-900">Skipped</span>
              </div>
              <span className="text-xl font-bold text-gray-600">{summaryData.skippedAnswers}</span>
            </div>

            <div className="flex items-center justify-between p-4 bg-blue-50 rounded-lg">
              <div className="flex items-center">
                <span className="text-2xl mr-3">⏱️</span>
                <span className="font-medium text-gray-900">Total Time</span>
              </div>
              <span className="text-xl font-bold text-blue-600">{formatTime(summaryData.totalTime)}</span>
            </div>
          </div>
        </div>

        {/* Topic Progress */}
        {summaryData.topicProgress && (
          <div className="bg-white rounded-lg shadow-md p-6 mb-8">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">🎯 Topic Progress</h2>
            <div className="p-4 bg-gray-50 rounded-lg">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-lg font-semibold text-gray-900">{summaryData.topicProgress.topic}</h3>
                <span className={`px-3 py-1 rounded-full text-sm font-medium ${getMasteryColor(summaryData.topicProgress.masteryLevel)}`}>
                  {getMasteryIcon(summaryData.topicProgress.masteryLevel)} {summaryData.topicProgress.masteryLevel}
                </span>
              </div>
              <div className="space-y-2">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-gray-600">Topic Accuracy</span>
                  <span className="font-semibold text-gray-900">{summaryData.topicProgress.accuracy.toFixed(1)}%</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div
                    className="bg-blue-600 h-2 rounded-full transition-all"
                    style={{ width: `${summaryData.topicProgress.accuracy}%` }}
                  ></div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* AI Feedback */}
        <div className="bg-gradient-to-r from-blue-500 to-purple-600 rounded-lg p-6 text-white mb-8">
          <div className="flex items-start">
            <span className="text-3xl mr-4">🤖</span>
            <div className="flex-1">
              <h2 className="text-xl font-semibold mb-3">AI Feedback</h2>
              <div className="text-blue-100 whitespace-pre-wrap leading-relaxed">
                {summaryData.aiFeedback}
              </div>
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex gap-4">
          <button
            onClick={() => navigate('/practice')}
            className="flex-1 px-6 py-4 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-semibold text-lg"
          >
            Practice Again
          </button>
          <button
            onClick={() => navigate('/dashboard')}
            className="flex-1 px-6 py-4 bg-white text-gray-900 border-2 border-gray-300 rounded-lg hover:bg-gray-50 transition-colors font-semibold text-lg"
          >
            Back to Dashboard
          </button>
        </div>
      </div>
    </div>
  );
};

export default PracticeSummaryPage;
